import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PropTypes } from "prop-types";
import { useDispatch } from "react-redux";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../../../redux/actions/snackbarActions";
import { useTrainingLocations } from "../../../../hooks/trainingLocations";
import {
  statusTypes,
  trainingLocationTypes
} from "../../../../constants/index";
import { Add } from "@mui/icons-material";
import { useLocation, useLocations } from "../../../../hooks/locations";
import AddNewTrainingLocationModal from "./AddNewTrainingLocationModal";
import MenuButton from "../../../../components/Buttons/MenuButton";

const AddTrainingLocationButton = ({ onSave, buttonProps }) => {
  const dispatch = useDispatch();
  const { createTrainingLocation, isSaving } = useTrainingLocations();

  const { updateLocation } = useLocation();
  const [locations, setLocations] = useState([]);
  const { getLocations, isLoading } = useLocations();

  useEffect(() => {
    if (!isSaving) {
      getLocations({
        filters: {
          withoutTrainingLocations: true
        }
      }).then(({ data }) => setLocations(data));
    }
  }, [isSaving]);

  const { t } = useTranslation();
  const [trainingLocationType, setTrainingLocationType] = useState(
    trainingLocationTypes.internal
  ); // Set to either external or internal based on what the user selects when they click the Add New button
  const [addNewModalOpen, setAddNewModalOpen] = useState(false);

  const handleCreateTrainingLocation = newTrainingLocationData => {
    return createTrainingLocation(newTrainingLocationData)
      .then(() => {
        getLocations();
        dispatch(
          enqueueSuccessSnackbar({
            message: t("manageCourses.createTrainingLocationSuccessMsg")
          })
        );
        onSave();
      })
      .catch(() =>
        dispatch(
          enqueueErrorSnackbar({
            message: t("manageCourses.createTrainingLocationErrorMsg")
          })
        )
      );
  };

  const handleUpdateLocation = (locationId, phone) => {
    const selectedLocation = locations.find(({ id }) => id === locationId);
    if (!!phone && selectedLocation.phone !== phone) {
      updateLocation(locationId, { phone });
    }
  };

  const onCreateTrainingLocation = values => {
    const { locationId, ...trainingLocationData } = values;
    let newTrainingLocationData = trainingLocationData;
    if (!!locationId) {
      newTrainingLocationData.location = {
        id: locationId,
        type: "location"
      };
      handleUpdateLocation(locationId, trainingLocationData.phone);
    }
    newTrainingLocationData.statusType = {
      id: statusTypes.active,
      type: "statusType"
    };
    handleCreateTrainingLocation(newTrainingLocationData).then(() =>
      setAddNewModalOpen(false)
    );
  };

  const handleAddNewTrainingLocationClick = trainingLocationType => {
    setTrainingLocationType(trainingLocationType);
    setAddNewModalOpen(true);
  };

  return (
    <>
      <MenuButton
        startIcon={<Add />}
        onClick={handleAddNewTrainingLocationClick}
        menuItems={Object.values(trainingLocationTypes).map(
          trainingLocationType => ({
            label: t("manageCourses.trainingLocationType", {
              context: trainingLocationType
            }),
            value: trainingLocationType
          })
        )}
        {...buttonProps}
      >
        {t("courses.manageTrainingLocations.addNew")}
      </MenuButton>
      <AddNewTrainingLocationModal
        open={addNewModalOpen}
        onClose={() => setAddNewModalOpen(false)}
        onCreateTrainingLocation={onCreateTrainingLocation}
        locations={locations}
        isCreating={isSaving}
        isLoadingLocations={isLoading}
        defaultTrainingLocationType={trainingLocationType}
      />
    </>
  );
};

export default AddTrainingLocationButton;

AddTrainingLocationButton.propTypes = {
  onSave: PropTypes.func,
  // Props to be passed to Material UI button
  buttonProps: PropTypes.object
};
